import { dayjs, getAppTimezone } from '@lib/dayjs'

// Serializable date range used at picker / filter boundaries.
// Values are UTC ISO 8601 strings — safe for Redux, URL params, IDB.
export type IsoDateRange = { from?: string | null; to?: string | null }

// Picker Date (browser-local wall-clock) -> backend UTC ISO, interpreted in the user's tz.
//
// Example — user tz: "Asia/Dubai" (UTC+4), browser tz: "America/Los_Angeles":
//   input:  new Date(2026, 3, 19, 0, 0, 0)        // 2026-04-19 00:00 (browser-local)
//   output: "2026-04-18T20:00:00.000Z"            // 2026-04-19 00:00 Dubai -> UTC
export const pickerDateToBackendIso = (date: Date): string => {
  const wall = dayjs(date).format('YYYY-MM-DDTHH:mm:ss')

  return dayjs.tz(wall, getAppTimezone()).toISOString()
}

// Backend UTC ISO -> picker Date (browser-local wall-clock) matching the user-tz rendering.
//
// Example — user tz: "Asia/Dubai" (UTC+4), browser tz: "America/Los_Angeles":
//   input:  "2026-04-18T20:00:00.000Z"            // 2026-04-19 00:00 Dubai
//   output: new Date(2026, 3, 19, 0, 0, 0)        // 2026-04-19 00:00 (browser-local)
export const backendIsoToPickerDate = (iso: string | Date): Date => {
  const t = dayjs.utc(iso).tz(getAppTimezone())

  return new Date(t.year(), t.month(), t.date(), t.hour(), t.minute(), t.second(), t.millisecond())
}

// Dayjs (in any tz) -> picker Date whose wall-clock matches the Dayjs rendered in the user's tz.
// Used for feeding presets (Today, Yesterday, ...) into react-day-picker.
//
// Example — user tz: "Asia/Dubai", browser tz: "America/Los_Angeles", preset "Today":
//   input:  dayjs().tz("Asia/Dubai").startOf("day")   // e.g. 2026-04-20 00:00 Dubai
//   output: new Date(2026, 3, 20, 0, 0, 0)            // 2026-04-20 00:00 (browser-local)
export const tzDayjsToPickerDate = (d: dayjs.Dayjs): Date => {
  const t = d.tz(getAppTimezone())

  return new Date(t.year(), t.month(), t.date(), t.hour(), t.minute(), t.second(), t.millisecond())
}
